import React from 'react';
import * as Icons from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { useFilter } from '../../context/FilterContext';
import { scrollToAnchor } from './Navigation';

export default function Sidebar() {
  const { data } = useSite();
  const { category, setCategory, setQuery } = useFilter();
  const items = data.sidebar || [];
  const active = category || items[0]?.id;

  const handleClick = (item) => {
    const id = (item.id || '').toLowerCase();
    const label = (item.label || '').toLowerCase();
    const isReset = id.startsWith('all') || id === 'home' || label.includes('all ') || label.startsWith('all') || label === 'home';
    const isB2B = id.includes('b2b') || label.includes('b2b');
    const isB2C = id.includes('b2c') || label.includes('b2c');

    if (isB2B || isB2C) {
      setCategory(isB2B ? 'b2b' : 'b2c');
      setQuery('');
      setTimeout(() => {
        const el = document.getElementById('exchanges');
        if (el) {
          const isMobile = window.innerWidth < 768;
          const offset = isMobile ? 120 : 90;
          const top = el.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
        }
      }, 40);
      return;
    }

    setCategory(isReset ? 'home' : item.id);
    setQuery('');
    const link = (item.link || '').trim();
    if (link) {
      // External URL → open in a new tab
      if (/^https?:\/\//i.test(link)) { window.open(link, '_blank', 'noopener'); return; }
      // Tel / mailto / whatsapp → let the browser handle
      if (/^(tel:|mailto:|whatsapp:)/i.test(link)) { window.location.href = link; return; }
      // In-page anchor (#section) → smooth-scroll
      if (link.startsWith('#')) { scrollToAnchor(link); return; }
      // Bare slash → top of page
      if (link === '/' || link === '') { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
      // Anything else (e.g. /contact) → in-app navigate
      window.location.assign(link); return;
    }
    // No link supplied → default behaviour: filter + scroll to the services grid
    const anchor = document.querySelector('[data-testid="gamecards-grid"], [data-testid="products-empty"]');
    if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.dispatchEvent(new CustomEvent('reddy:navigate', { detail: { id: item.id, label: item.label } }));
  };

  return (
    <div className="bg-zinc-950/60 rounded-lg border border-zinc-900 overflow-hidden">
      <div className="max-h-[calc(100vh-160px)] overflow-y-auto no-scrollbar py-2">
        {items.map((item, idx) => {
          const Icon = Icons[item.icon] || Icons.Circle;
          const isActive = active === item.id;
          return (
            <button
              key={item.id}
              onClick={() => handleClick(item)}
              data-testid={`sidebar-${item.id}`}
              style={{ animationDelay: `${idx * 30}ms` }}
              className={`slide-in-left w-full flex items-center gap-3 px-4 py-3.5 text-[13px] font-semibold border-b border-zinc-900/80 transition-all click-ripple
                ${isActive
                  ? 'bg-[color:var(--gold)]/10 text-[color:var(--gold)] olive-glow'
                  : 'text-zinc-200 hover:bg-zinc-900/60 hover:text-[color:var(--gold)]'}`}
            >
              <Icon size={16} className={isActive ? 'text-[color:var(--gold)]' : 'text-zinc-300'} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
